"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import dynamic from "next/dynamic";
import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import { SeoHeading } from "@/components/common/seo-heading";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import { hasData } from "@/helpers/has-data";
import { FileText, BookOpen, ClipboardList, FileSpreadsheet, MessageSquare, Users, Download } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";

// Dynamic import of the popup to avoid SSR hydration issues
const ResourceDownloadPopup = dynamic(() => import("@/components/forms/resource-download-popup").then(mod => mod.ResourceDownloadPopup), {
  ssr: false
});
type FreeResourcesDownloadsProps = {
  section: PageSections;
  data: {
    heading: string;
    seoHeading: string;
    paragraph: string;
    serviceId?: number;
    freeResourcesDownloads: ResourceType[];
  };
};
type ResourceType = {
  id: number;
  heading: string;
  description: string;
  icon: string;
  buttonText: string;
  resourceBannerUrl: string;
  isUltimate: boolean;
};
const getCardStyle = (index: number) => {
  const styles = [{
    bg: "bg-emerald-600 text-white dark:bg-emerald-700",
    color: "text-emerald-600 dark:text-emerald-400",
    icon: FileText
  }, {
    bg: "bg-blue-600 text-white dark:bg-blue-750",
    color: "text-blue-600 dark:text-blue-400",
    icon: BookOpen
  }, {
    bg: "bg-purple-600 text-white dark:bg-purple-700",
    color: "text-purple-600 dark:text-purple-400",
    icon: ClipboardList
  }, {
    bg: "bg-orange-500 text-white dark:bg-orange-600",
    color: "text-orange-500 dark:text-orange-400",
    icon: FileSpreadsheet
  }, {
    bg: "bg-teal-500 text-white dark:bg-teal-600",
    color: "text-teal-500 dark:text-teal-400",
    icon: MessageSquare
  }, {
    bg: "bg-amber-500 text-white dark:bg-amber-600",
    color: "text-amber-500 dark:text-amber-400",
    icon: Users
  }];
  const style = styles[index % styles.length];
  return style;
};
const SmallCard = t0 => {
  const $ = _c(37);
  const {
    item,
    index,
    serviceId
  } = t0;
  const [open, setOpen] = useState(false);
  let CardIcon;
  let T0;
  let T1;
  let t1;
  let t2;
  if ($[0] !== index) {
    const cardStyle = getCardStyle(index);
    CardIcon = cardStyle.icon;
    T1 = Card;
    t2 = "group justify-between gap-1 shadow-xs transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md";
    T0 = CardHeader;
    t1 = cn("relative mx-auto mb-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105", cardStyle.bg);
    $[0] = index;
    $[1] = CardIcon;
    $[2] = T0;
    $[3] = T1;
    $[4] = t1;
    $[5] = t2;
  } else {
    CardIcon = $[1];
    T0 = $[2];
    T1 = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== CardIcon) {
    t3 = <CardIcon className="h-5 w-5" />;
    $[6] = CardIcon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== item.heading) {
    t5 = <Heading as="h4" className="line-clamp-2 flex min-h-10 items-center justify-center truncate text-center text-sm leading-snug font-bold">{item.heading}</Heading>;
    $[11] = item.heading;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== T0 || $[14] !== t4 || $[15] !== t5) {
    t6 = <T0>{t4}{t5}</T0>;
    $[13] = T0;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== item.description) {
    t7 = <CardContent className="text-center"><Text className="line-clamp-3 flex-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{item.description}</Text></CardContent>;
    $[17] = item.description;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setOpen(true);
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Download className="h-3.5 w-3.5" />;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = item.buttonText || "Download";
  let t11;
  if ($[21] !== t10) {
    t11 = <CardFooter className="bg-card justify-center border-0 py-2"><Button onClick={t8} variant="link">{t9}{t10}</Button></CardFooter>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== T1 || $[24] !== t11 || $[25] !== t2 || $[26] !== t6 || $[27] !== t7) {
    t12 = <T1 className={t2}>{t6}{t7}{t11}</T1>;
    $[23] = T1;
    $[24] = t11;
    $[25] = t2;
    $[26] = t6;
    $[27] = t7;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = `Download ${item.heading}`;
  let t14;
  if ($[29] !== item.id || $[30] !== open || $[31] !== serviceId || $[32] !== t13) {
    t14 = <ResourceDownloadPopup open={open} onOpenChange={setOpen} serviceId={serviceId} serviceResourceId={item.id} heading={t13} />;
    $[29] = item.id;
    $[30] = open;
    $[31] = serviceId;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t12 || $[35] !== t14) {
    t15 = <>{t12}{t14}</>;
    $[34] = t12;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
};
const UltimateGuideCard = t0 => {
  const $ = _c(17);
  const {
    guide,
    serviceId
  } = t0;
  const [open, setOpen] = useState(false);
  const t1 = guide.resourceBannerUrl ? `url(${guide.resourceBannerUrl})` : undefined;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      backgroundImage: t1,
      backgroundSize: "cover",
      backgroundPosition: "center"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute inset-0 z-0 bg-black/25" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(true);
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = guide.buttonText || "Download Ultimate Guide";
  let t6;
  if ($[4] !== t5) {
    t6 = <div className="relative z-10 flex h-full flex-col justify-between"><CardFooter className="mt-auto border-0 bg-transparent"><Button onClick={t4} className="w-full cursor-pointer rounded-lg bg-[#2e7d32] py-5 font-semibold text-white shadow-xs transition-all duration-200 hover:bg-[#1b5e20]">{t5}</Button></CardFooter></div>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t6) {
    t7 = <Card className="group relative flex h-full flex-col justify-between overflow-hidden shadow-xs transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" style={t2}>{t3}{t6}</Card>;
    $[6] = t2;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = `Download ${guide.heading}`;
  let t9;
  if ($[9] !== guide.id || $[10] !== open || $[11] !== serviceId || $[12] !== t8) {
    t9 = <ResourceDownloadPopup open={open} onOpenChange={setOpen} serviceId={serviceId} heading={t8} formType="RESOURCE_ALL_DOWNLOAD" serviceResourceId={guide.id} />;
    $[9] = guide.id;
    $[10] = open;
    $[11] = serviceId;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <>{t7}{t9}</>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
};

// Maps of grid column classes to dynamically prevent empty spacing when cards are omitted
const gridColsMapWithGuide: Record<number, string> = {
  1: "grid-cols-1 max-w-sm",
  2: "grid-cols-1 sm:grid-cols-2 max-w-xl",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-w-3xl",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-4",
  5: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5",
  6: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
};
const gridColsMapWithoutGuide: Record<number, string> = {
  1: "grid-cols-1 max-w-md mx-auto",
  2: "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-w-4xl mx-auto",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 max-w-5xl",
  5: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
  6: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
};
const FreeResourcesDownloads = t0 => {
  const $ = _c(84);
  const {
    section,
    data
  } = t0;
  if (!hasData(data)) {
    return null;
  }
  const t1 = data.freeResourcesDownloads;
  let T0;
  let hasUltimateGuide;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let ultimateGuide;
  if ($[0] !== data.freeResourcesDownloads || $[1] !== data.heading || $[2] !== data.paragraph || $[3] !== data.seoHeading || $[4] !== data.serviceId || $[5] !== section.sectionClass || $[6] !== section.sectionId) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const resourceList = t1.filter(_temp);
      let t10;
      if ($[18] !== data.freeResourcesDownloads) {
        t10 = data.freeResourcesDownloads.find(_temp2);
        $[18] = data.freeResourcesDownloads;
        $[19] = t10;
      } else {
        t10 = $[19];
      }
      ultimateGuide = t10;
      const hasSmallCards = data.freeResourcesDownloads.length > 0;
      hasUltimateGuide = !!ultimateGuide;
      let t11;
      if ($[20] !== hasUltimateGuide) {
        t11 = count => {
          if (hasUltimateGuide) {
            if (count >= 6) {
              return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6";
            }
            return gridColsMapWithGuide[count] || "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6";
          } else {
            if (count >= 6) {
              return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6";
            }
            return gridColsMapWithoutGuide[count] || "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6";
          }
        };
        $[20] = hasUltimateGuide;
        $[21] = t11;
      } else {
        t11 = $[21];
      }
      const getGridColsClass = t11;
      if (!hasSmallCards && !hasUltimateGuide) {
        let t12;
        if ($[22] !== data.heading || $[23] !== data.seoHeading) {
          t12 = <SeoHeading heading={data.heading} seoHeading={data.seoHeading} />;
          $[22] = data.heading;
          $[23] = data.seoHeading;
          $[24] = t12;
        } else {
          t12 = $[24];
        }
        let t13;
        if ($[25] !== data.paragraph) {
          t13 = data.paragraph && <Text className="text-muted-foreground mt-2 max-w-3xl">{data.paragraph}</Text>;
          $[25] = data.paragraph;
          $[26] = t13;
        } else {
          t13 = $[26];
        }
        let t14;
        if ($[27] !== t12 || $[28] !== t13) {
          t14 = <div className="max-w-4xl">{t12}{t13}</div>;
          $[27] = t12;
          $[28] = t13;
          $[29] = t14;
        } else {
          t14 = $[29];
        }
        let t15;
        if ($[30] !== section.sectionClass || $[31] !== section.sectionId || $[32] !== t14) {
          t15 = <Section id={section.sectionId} className={section.sectionClass}>{t14}</Section>;
          $[30] = section.sectionClass;
          $[31] = section.sectionId;
          $[32] = t14;
          $[33] = t15;
        } else {
          t15 = $[33];
        }
        t9 = t15;
        break bb0;
      }
      if (hasUltimateGuide && !hasSmallCards) {
        let t12;
        if ($[34] !== data.heading || $[35] !== data.seoHeading) {
          t12 = <SeoHeading heading={data.heading} seoHeading={data.seoHeading} />;
          $[34] = data.heading;
          $[35] = data.seoHeading;
          $[36] = t12;
        } else {
          t12 = $[36];
        }
        let t13;
        if ($[37] !== data.paragraph) {
          t13 = data.paragraph && <Text className="text-muted-foreground mt-2 max-w-3xl">{data.paragraph}</Text>;
          $[37] = data.paragraph;
          $[38] = t13;
        } else {
          t13 = $[38];
        }
        let t14;
        if ($[39] !== t12 || $[40] !== t13) {
          t14 = <div className="mb-8 max-w-4xl">{t12}{t13}</div>;
          $[39] = t12;
          $[40] = t13;
          $[41] = t14;
        } else {
          t14 = $[41];
        }
        let t15;
        if ($[42] !== data.serviceId || $[43] !== ultimateGuide) {
          t15 = <div className="flex w-full justify-center"><div className="w-full max-w-lg"><UltimateGuideCard guide={ultimateGuide} serviceId={data.serviceId} /></div></div>;
          $[42] = data.serviceId;
          $[43] = ultimateGuide;
          $[44] = t15;
        } else {
          t15 = $[44];
        }
        let t16;
        if ($[45] !== section.sectionClass || $[46] !== section.sectionId || $[47] !== t14 || $[48] !== t15) {
          t16 = <Section id={section.sectionId} className={section.sectionClass}>{t14}{t15}</Section>;
          $[45] = section.sectionClass;
          $[46] = section.sectionId;
          $[47] = t14;
          $[48] = t15;
          $[49] = t16;
        } else {
          t16 = $[49];
        }
        t9 = t16;
        break bb0;
      }
      T0 = Section;
      t7 = section.sectionId;
      t8 = section.sectionClass;
      const t12 = hasUltimateGuide ? "lg:grid-cols-12" : "grid-cols-1";
      if ($[50] !== t12) {
        t6 = cn("grid grid-cols-1 gap-5", t12);
        $[50] = t12;
        $[51] = t6;
      } else {
        t6 = $[51];
      }
      const t13 = hasUltimateGuide ? "lg:col-span-8 xl:col-span-9" : "w-full";
      if ($[52] !== t13) {
        t4 = cn("flex h-full flex-col justify-between", t13);
        $[52] = t13;
        $[53] = t4;
      } else {
        t4 = $[53];
      }
      let t14;
      if ($[54] !== data.heading || $[55] !== data.seoHeading) {
        t14 = <SeoHeading heading={data.heading} seoHeading={data.seoHeading} />;
        $[54] = data.heading;
        $[55] = data.seoHeading;
        $[56] = t14;
      } else {
        t14 = $[56];
      }
      let t15;
      if ($[57] !== data.paragraph) {
        t15 = data.paragraph && <Text className="text-muted-foreground mt-2 max-w-3xl">{data.paragraph}</Text>;
        $[57] = data.paragraph;
        $[58] = t15;
      } else {
        t15 = $[58];
      }
      if ($[59] !== t14 || $[60] !== t15) {
        t5 = <div className="mb-6">{t14}{t15}</div>;
        $[59] = t14;
        $[60] = t15;
        $[61] = t5;
      } else {
        t5 = $[61];
      }
      t2 = cn("grid w-full gap-5", getGridColsClass(resourceList.length));
      let t16;
      if ($[62] !== data.serviceId) {
        t16 = (item_1, idx) => <SmallCard key={item_1.id} item={item_1} index={idx} serviceId={data.serviceId} />;
        $[62] = data.serviceId;
        $[63] = t16;
      } else {
        t16 = $[63];
      }
      t3 = resourceList.map(t16);
    }
    $[0] = data.freeResourcesDownloads;
    $[1] = data.heading;
    $[2] = data.paragraph;
    $[3] = data.seoHeading;
    $[4] = data.serviceId;
    $[5] = section.sectionClass;
    $[6] = section.sectionId;
    $[7] = T0;
    $[8] = hasUltimateGuide;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = ultimateGuide;
  } else {
    T0 = $[7];
    hasUltimateGuide = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
    ultimateGuide = $[17];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[64] !== t2 || $[65] !== t3) {
    t10 = <div className={t2}>{t3}</div>;
    $[64] = t2;
    $[65] = t3;
    $[66] = t10;
  } else {
    t10 = $[66];
  }
  let t11;
  if ($[67] !== t10 || $[68] !== t4 || $[69] !== t5) {
    t11 = <div className={t4}>{t5}{t10}</div>;
    $[67] = t10;
    $[68] = t4;
    $[69] = t5;
    $[70] = t11;
  } else {
    t11 = $[70];
  }
  let t12;
  if ($[71] !== data.serviceId || $[72] !== hasUltimateGuide || $[73] !== ultimateGuide) {
    t12 = hasUltimateGuide && <div className="flex flex-col justify-end lg:col-span-4 xl:col-span-3"><UltimateGuideCard guide={ultimateGuide} serviceId={data.serviceId} /></div>;
    $[71] = data.serviceId;
    $[72] = hasUltimateGuide;
    $[73] = ultimateGuide;
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  let t13;
  if ($[75] !== t11 || $[76] !== t12 || $[77] !== t6) {
    t13 = <div className={t6}>{t11}{t12}</div>;
    $[75] = t11;
    $[76] = t12;
    $[77] = t6;
    $[78] = t13;
  } else {
    t13 = $[78];
  }
  let t14;
  if ($[79] !== T0 || $[80] !== t13 || $[81] !== t7 || $[82] !== t8) {
    t14 = <T0 id={t7} className={t8}>{t13}</T0>;
    $[79] = T0;
    $[80] = t13;
    $[81] = t7;
    $[82] = t8;
    $[83] = t14;
  } else {
    t14 = $[83];
  }
  return t14;
};
export default FreeResourcesDownloads;
function _temp(item) {
  return item.isUltimate !== true;
}
function _temp2(item_0) {
  return item_0.isUltimate === true;
}